import { createContext, useEffect, useState, FC } from 'react';
import { FormatSearchResultsReturn } from '@twiliointernal/e2-commons-shared-definitions';

import { SupportApi } from '@twiliointernal/e2-commons-shared-api';
import { useSegment } from '@twiliointernal/e2-commons-shared-hooks';

import { constants, types } from './definitions';
import { formatSearchResults } from '../../utils';
import { logger } from '@twiliointernal/e2-commons-shared-libs';
import { useUserDetails } from '../../hooks';

export const SearchResultsContext =
  createContext<types.UseGetSearchResultsReturn>({
    loading: false,
    updateQueryText: () => {},
  });

const useProvideSearchResults = (): types.UseGetSearchResultsReturn => {
  const { account, user, isLoading, isLoggedIn } = useUserDetails();
  const { trackEvent } = useSegment({
    accountSid: account?.sid,
    userSid: user?.sid,
    isLoading,
    isLoggedIn,
  });
  const [searchValue, updateQueryText] = useState('');
  const [loading, updateLoading] = useState<boolean>(false);
  const [error, updateError] = useState<Error | undefined>(undefined);
  const [searchResults, updateSearchResults] =
    useState<FormatSearchResultsReturn>();

  // Search query submitted
  useEffect(() => {
    if (searchValue) {
      updateLoading(true);
      (async (): Promise<void> => {
        try {
          const response = await SupportApi.search(searchValue);

          const formattedResults = formatSearchResults(
            searchValue,
            response.data,
            trackEvent,
          );

          updateSearchResults(formattedResults);
        } catch (err) {
          logger.error(`${constants.LOG_PREFIX}::${err}`);
          updateError(err as Error);
        }
        updateLoading(false);
      })();
    }
  }, [searchValue]);

  return {
    error,
    loading,
    searchResults,
    updateQueryText,
  };
};

export const SearchResultsProvider: FC<types.UseGetSearchResultsProps> = ({
  children,
}: types.UseGetSearchResultsProps) => {
  const searchResultsReturn = useProvideSearchResults();
  return (
    <SearchResultsContext.Provider value={searchResultsReturn}>
      {children}
    </SearchResultsContext.Provider>
  );
};
